import { useGetApPayeesData } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useGetTargetApplicantsData } from '@/context/services/invoice_reports/TargetApplicants.service';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface UseConfigFormProps {
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings;
}

export const useConfigForm = () => {
  const { t } = useTranslation();

  const checkLengthValidationRules = useMemo(
    () => ({
      maxLength: {
        value: 100,
        message: t('search.validation.max_length', { maxLength: 100 }),
      },
    }),
    [t]
  );

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const sizeSmall = 'small' as const;

  const customProps = useMemo(
    () => ({
      labels: {
        query: {
          text: t('invoice_reports.search.keyword'),
          tooltip: t('invoice_reports.search.keyword_tooltip'),
        },
        number: {
          text: t('invoice_reports.search.number'),
        },
        status: {
          text: t('invoice_reports.search.status'),
        },
        agent_applicant: {
          text: t('invoice_reports.search.agent_applicant_office_member'),
        },
        ap_payee: {
          text: t('invoice_reports.search.ap_payee'),
        },
        total_amount: {
          text: t('invoice_reports.search.total_amount'),
        },
        payment_status: {
          text: t('invoice_reports.search.payment_status'),
        },
        mf_file: {
          text: t('invoice_reports.search.mf_file'),
        },
        created_date: {
          text: t('invoice_reports.search.created_date'),
        },
        submitted_date: {
          text: t('invoice_reports.search.submitted_date'),
        },
        approved_date: {
          text: t('invoice_reports.search.approved_date'),
        },
        book_date: {
          text: t('invoice_reports.search.book_date'),
        },
        due_date: {
          text: t('invoice_reports.search.due_date'),
        },
        payment_date: {
          text: t('invoice_reports.search.payment_date'),
        },
        reset_button: t('invoice_reports.search.reset_button'),
        search_button: t('invoice_reports.search.search_button'),
      },
      selectProps: {
        config: { maxTagCount: 1, placeholder: '', size: sizeSmall },

        configApPayee: {
          inputPlaceholder: t('invoice_reports.search.ap_payee.placeholder'),
          size: sizeSmall,
        },
        configAgentApplicant: {
          size: sizeSmall,
          maxTagCount: 1,
          inputPlaceholder: '',
          placeholder: '',
        },
      },
      inputProps: {
        query: {
          search: true,
          size: sizeSmall,
        },
        number: {
          size: sizeSmall,
          search: false,
        },
      },
    }),
    [t]
  );

  return {
    customProps,
    checkLengthValidationRules,
  };
};

const useInitialData = <T extends BaseOption>(
  ids: string[],
  fetchFunction: (ids: string[]) => Promise<T[]>
) => {
  const [options, setOptions] = useState<T[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once - check if we've already fetched
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true; // Mark as fetched
      if (ids.length === 0) {
        setOptions([]);
        setIsLoaded(true);
        return;
      }
      const fetchData = async () => {
        try {
          const data = await fetchFunction(ids);
          setOptions(data);
        } catch (err) {
          setOptions([]);
        } finally {
          setIsLoaded(true);
        }
      };

      fetchData();
    }
  }, [ids, fetchFunction]);

  return { options, isLoaded };
};

export const useApPayeeInitialData = (payeeIds: string[]) => {
  const { fetchApPayees } = useGetApPayeesData();
  const { options } = useInitialData(payeeIds, fetchApPayees);

  return options;
};

export const useAgentApplicantInitialData = (memberIds: string[]) => {
  const { fetchTargetApplicants } = useGetTargetApplicantsData();
  const { options, isLoaded } = useInitialData(
    memberIds,
    fetchTargetApplicants
  );

  return { options, isLoaded };
};
